import type {
  Disclosure,
  DisclosureDefinition,
  DisclosurePageKey,
  DisclosurePageOutput,
  DisclosureSummaryOutput,
  NewDisclosure,
} from '@/types';
import { api } from './api';

const disclosureApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getDisclosures: builder.query<Disclosure[], number>({
      query: (inventoryId) => `inventories/${inventoryId}/disclosures`,
      providesTags: ['Disclosures'],
    }),
    getDisclosure: builder.query<Disclosure, number>({
      query: (id) => `disclosures/${id}`,
      providesTags: ['Disclosures'],
    }),
    // `/summary` and `/page/<key>` are POST "simulate" endpoints: the
    // body carries the current (possibly unsaved) `definition` so village
    // renders the report against it without persisting. Because the
    // definition is part of the query arg (the cache key), editing the
    // period re-runs these queries and the report recomputes live; Save
    // persists separately. An absent definition renders the saved
    // disclosure. The community definition is wire-identical FE<->village,
    // so it's sent as-is. Mirrors suit's disclosure queries.
    getDisclosureSummary: builder.query<
      DisclosureSummaryOutput,
      { id: number; definition?: DisclosureDefinition }
    >({
      query: ({ id, definition }) => ({
        url: `disclosures/${id}/summary`,
        method: 'POST',
        body: definition ? { definition } : {},
      }),
      providesTags: ['Disclosures'],
    }),
    getDisclosurePage: builder.query<
      DisclosurePageOutput,
      { id: number; key: DisclosurePageKey; definition?: DisclosureDefinition }
    >({
      query: ({ id, key, definition }) => ({
        url: `disclosures/${id}/page/${key}`,
        method: 'POST',
        body: definition ? { definition } : {},
      }),
      providesTags: ['Disclosures'],
    }),
    newDisclosure: builder.mutation<Disclosure, NewDisclosure>({
      query: ({ inventoryId, name, definition }) => ({
        url: `inventories/${inventoryId}/disclosures`,
        method: 'POST',
        body: { name, definition },
      }),
      invalidatesTags: ['Disclosures'],
    }),
    updateDisclosure: builder.mutation<
      Disclosure,
      { id: number; name: string; definition: DisclosureDefinition }
    >({
      query: ({ id, name, definition }) => ({
        url: `disclosures/${id}`,
        method: 'POST',
        body: { name, definition },
      }),
      invalidatesTags: ['Disclosures'],
    }),
  }),
});

export const {
  useGetDisclosuresQuery,
  useGetDisclosureQuery,
  useGetDisclosureSummaryQuery,
  useGetDisclosurePageQuery,
  useNewDisclosureMutation,
  useUpdateDisclosureMutation,
} = disclosureApi;
